import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CalendarClock, Plus, CalendarCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const callColor = (s) => ({
  Hold: 'bg-gray-100 text-gray-600', Released: 'bg-sky-100 text-sky-700',
  Completed: 'bg-green-100 text-green-700', Skipped: 'bg-amber-100 text-amber-700',
}[s] || 'bg-gray-100');
const EMPTY = { description: '', plan_type: 'Time-based', equipment: '', functional_location: '', task_list: '', order_type: 'Preventive', cycle_length: 30, cycle_unit: 'Days', call_horizon: 80, start_date: '' };

const MaintenancePlans = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('maintenance_plan');
  const [rows, setRows] = useState([]);
  const [taskLists, setTaskLists] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/pm/maintenance-plans`, { withCredentials: true }); setRows(res.data); }
    catch (e) { toast.error('Failed to load maintenance plans'); }
    finally { setLoading(false); }
  }, []);
  const loadAux = useCallback(async () => {
    try {
      const [tl, eq] = await Promise.all([
        axios.get(`${API}/pm/task-lists`, { withCredentials: true }),
        axios.get(`${API}/pm/equipment`, { withCredentials: true }),
      ]);
      setTaskLists(tl.data); setEquipment(eq.data);
    } catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => { load(); loadAux(); }, [load, loadAux]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/pm/maintenance-plans`, {
        description: form.description, plan_type: form.plan_type,
        equipment: form.equipment || null, functional_location: form.functional_location || null,
        task_list: form.task_list || null, order_type: form.order_type,
        cycle_length: parseInt(form.cycle_length, 10) || 30, cycle_unit: form.cycle_unit,
        call_horizon: parseInt(form.call_horizon, 10) || 80, start_date: form.start_date || null,
      }, { withCredentials: true });
      toast.success('Maintenance plan created'); setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  const schedule = async (number, force) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/pm/maintenance-plans/${number}/schedule`, { force }, { withCredentials: true });
      const last = res.data.calls[res.data.calls.length - 1];
      toast.success(last?.order_number ? `Scheduled — order ${last.order_number} generated` : `Call scheduled for ${last?.planned_date}`);
      setDetail(res.data); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Scheduling failed'); }
    finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="maintenance-plans-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-sky-100 text-sky-700"><CalendarClock className="h-5 w-5" /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Maintenance Plans</h1>
              <p className="text-gray-600 text-sm mt-0.5">Preventive scheduling that generates work orders automatically.</p>
            </div>
          </div>
          {writable && <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="new-plan-btn"><Plus className="h-4 w-4 mr-2" />New Plan</Button>}
        </div>

        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Plan #</TableHead><TableHead>Description</TableHead><TableHead>Type</TableHead>
              <TableHead>Cycle</TableHead><TableHead>Next Due</TableHead><TableHead className="text-right">Calls</TableHead>
              <TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">Loading...</TableCell></TableRow>
                : rows.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">No maintenance plans yet.</TableCell></TableRow>
                : rows.map((r) => (
                  <TableRow key={r.plan_number} data-testid={`plan-row-${r.plan_number}`}>
                    <TableCell className="font-medium">{r.plan_number}</TableCell>
                    <TableCell>{r.description}</TableCell>
                    <TableCell>{r.plan_type}</TableCell>
                    <TableCell>{r.cycle_length} {r.cycle_unit}</TableCell>
                    <TableCell>{r.next_due_date || '—'}</TableCell>
                    <TableCell className="text-right">{r.calls?.length || 0}</TableCell>
                    <TableCell><Badge variant={r.status === 'Active' ? 'default' : 'secondary'}>{r.status}</Badge></TableCell>
                    <TableCell><Button size="sm" variant="outline" onClick={() => setDetail(r)} data-testid={`open-plan-${r.plan_number}`}>Open</Button></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Create */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Maintenance Plan</DialogTitle>
            <DialogDescription>Schedule recurring preventive maintenance.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2"><Label>Description *</Label><Input data-testid="plan-description" value={form.description} onChange={(e) => set('description', e.target.value)} /></div>
            <div>
              <Label>Plan Type</Label>
              <Select value={form.plan_type} onValueChange={(v) => set('plan_type', v)}>
                <SelectTrigger data-testid="plan-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Time-based">Time-based</SelectItem>
                  <SelectItem value="Performance-based">Performance-based</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Equipment</Label>
              <Select value={form.equipment || 'none'} onValueChange={(v) => set('equipment', v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="plan-equipment"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {equipment.map((eq) => <SelectItem key={eq.equipment_number} value={eq.equipment_number}>{eq.equipment_number} — {eq.description}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Task List</Label>
              <Select value={form.task_list || 'none'} onValueChange={(v) => set('task_list', v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="plan-tasklist"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {taskLists.map((tl) => <SelectItem key={tl.task_list_number} value={tl.task_list_number}>{tl.task_list_number} — {tl.description}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Order Type</Label>
              <Select value={form.order_type} onValueChange={(v) => set('order_type', v)}>
                <SelectTrigger data-testid="plan-ordertype"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Preventive">Preventive</SelectItem>
                  <SelectItem value="Calibration">Calibration</SelectItem>
                  <SelectItem value="Corrective">Corrective</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Cycle Length</Label><Input type="number" data-testid="plan-cyclelength" value={form.cycle_length} onChange={(e) => set('cycle_length', e.target.value)} /></div>
              <div>
                <Label>Unit</Label>
                <Select value={form.cycle_unit} onValueChange={(v) => set('cycle_unit', v)}>
                  <SelectTrigger data-testid="plan-cycleunit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Days">Days</SelectItem>
                    <SelectItem value="Weeks">Weeks</SelectItem>
                    <SelectItem value="Months">Months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Call Horizon (%)</Label><Input type="number" data-testid="plan-horizon" value={form.call_horizon} onChange={(e) => set('call_horizon', e.target.value)} /></div>
            <div><Label>Start Date</Label><Input type="date" data-testid="plan-startdate" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.description} data-testid="save-plan-btn">{saving ? 'Saving...' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">{detail.plan_number}
                  <Badge variant={detail.status === 'Active' ? 'default' : 'secondary'}>{detail.status}</Badge>
                </DialogTitle>
                <DialogDescription>{detail.description} · {detail.plan_type} · every {detail.cycle_length} {detail.cycle_unit}</DialogDescription>
              </DialogHeader>
              <div className="text-sm space-y-1">
                <div><span className="text-gray-500">Equipment:</span> {detail.equipment || '—'}</div>
                <div><span className="text-gray-500">Task List:</span> {detail.task_list || '—'}</div>
                <div><span className="text-gray-500">Next Due:</span> {detail.next_due_date || 'Not scheduled'}</div>
              </div>

              {writable && detail.status === 'Active' && (
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => schedule(detail.plan_number, false)} disabled={busy} data-testid="schedule-plan-btn">
                    <CalendarCheck className="h-4 w-4 mr-1" />Schedule Next Call</Button>
                  <Button size="sm" variant="outline" onClick={() => schedule(detail.plan_number, true)} disabled={busy} data-testid="schedule-plan-force-btn">Force (create order now)</Button>
                </div>
              )}

              <div>
                <h4 className="font-semibold text-sm mb-1">Scheduled Calls</h4>
                {(detail.calls || []).length === 0 ? <p className="text-xs text-gray-400">No calls yet. Schedule to generate the first call.</p> : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Call</TableHead><TableHead>Planned Date</TableHead><TableHead>Order</TableHead><TableHead>Status</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {detail.calls.map((c) => (
                          <TableRow key={c.call_number} data-testid={`plan-call-${c.call_number}`}>
                            <TableCell className="font-medium">{c.call_number}</TableCell>
                            <TableCell>{c.planned_date}</TableCell>
                            <TableCell>{c.order_number || '—'}</TableCell>
                            <TableCell><span className={`px-2 py-1 rounded text-xs font-medium ${callColor(c.status)}`}>{c.status}</span></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default MaintenancePlans;
